if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();

      this.galleryVideoAutoplay = this.dataset.galleryVideoAutoplay === 'true';
      this._lastActivatedMediaId = null;
      this._activateResetTimer = null;
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      };
      this.mediaWrapper = this.closest('.product__media-wrapper');
      this.productGallery = this.querySelector('product-gallery');
      this.mobileMql = window.matchMedia('(max-width: 749px)');

      if (this.elements.viewer) {
        this._onSlideChanged = this.onSlideChanged.bind(this);
        this.elements.viewer.addEventListener('slideChanged', debounce(this._onSlideChanged, 150));
      }

      if (this.elements.thumbnails) {
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false, true));
        });
      }

      if (this.productGallery) {
        this.viewerPrevButton = this.productGallery.querySelector('.slider-buttons .slider-button--prev[name="previous"]');
        this.viewerNextButton = this.productGallery.querySelector('.slider-buttons .slider-button--next[name="next"]');
        if (this.viewerPrevButton && this.viewerNextButton) {
          this.viewerPrevButton.addEventListener('click', this.stepActiveMedia.bind(this, -1));
          this.viewerNextButton.addEventListener('click', this.stepActiveMedia.bind(this, 1));
        }
      }

      this._onMobileLayoutChange = this.onMobileLayoutChange.bind(this);
      if (this.mobileMql.addEventListener) {
        this.mobileMql.addEventListener('change', this._onMobileLayoutChange);
      } else {
        this.mobileMql.addListener(this._onMobileLayoutChange);
      }

      if (this.dataset.desktopLayout === 'thumbnail_slider' && window.matchMedia('(min-width: 750px)').matches) {
        this.removeListSemantic();
      }
    }

    disconnectedCallback() {
      this.teardownSlideObserver();
      this.teardownMobileScrollSync();
      if (this.mobileMql.removeEventListener) {
        this.mobileMql.removeEventListener('change', this._onMobileLayoutChange);
      } else {
        this.mobileMql.removeListener(this._onMobileLayoutChange);
      }
    }

    connectedCallback() {
      this.bindSliderDots();
      this.onMobileLayoutChange();

      if (!this.galleryVideoAutoplay || !this.elements.viewer) return;

      this.scheduleInitialAutoplay();
    }

    scheduleInitialAutoplay() {
      const run = () => {
        const slide = this.getActiveSlideFromScroll() || this.getActiveSlide();
        if (slide) this.handleSlideActivate(slide, { force: true });
      };

      run();
      window.setTimeout(run, 350);
      window.setTimeout(run, 1000);
    }

    onMobileLayoutChange() {
      if (this.mobileMql.matches) {
        this.setupSlideObserver();
        this.setupMobileScrollSync();
      } else {
        this.teardownSlideObserver();
        this.teardownMobileScrollSync();
      }
    }

    getSlider() {
      return this.elements.viewer?.querySelector('[id^="Slider-"]') || null;
    }

    getVisibleSlides() {
      if (!this.elements.viewer) return [];
      return Array.from(this.elements.viewer.querySelectorAll('[data-media-id]')).filter((slide) => {
        return slide.clientWidth > 0 && window.getComputedStyle(slide).display !== 'none';
      });
    }

    getActiveSlideFromScroll() {
      const slider = this.getSlider();
      const slides = this.getVisibleSlides();
      if (!slider || !slides.length) return null;

      const scrollLeft = slider.scrollLeft;
      let best = slides[0];
      let minDist = Infinity;

      slides.forEach((slide) => {
        const dist = Math.abs(slide.offsetLeft - scrollLeft);
        if (dist < minDist) {
          minDist = dist;
          best = slide;
        }
      });

      return best;
    }

    getActiveSlide() {
      return (
        this.elements.viewer?.querySelector('[data-media-id].is-active') ||
        this.getVisibleSlides()[0] ||
        null
      );
    }

    bindSliderDots() {
      if (!this.mediaWrapper) return;

      this.mediaWrapper.querySelectorAll('.slider-dots .slider-dot').forEach((dot, index) => {
        if (dot.dataset.galleryDotBound === 'true') return;
        dot.dataset.galleryDotBound = 'true';
        dot.addEventListener('click', () => {
          window.setTimeout(() => {
            const slide = this.getVisibleSlides()[index] || this.getActiveSlideFromScroll();
            if (slide) this.handleSlideActivate(slide, { force: true });
          }, 80);
        });
      });
    }

    setupMobileScrollSync() {
      const slider = this.getSlider();
      if (!slider || !this.galleryVideoAutoplay) return;

      this.teardownMobileScrollSync();

      let ticking = false;
      this._scrollHandler = () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(() => {
          ticking = false;
          const slide = this.getActiveSlideFromScroll();
          if (slide) this.handleSlideActivate(slide);
        });
      };

      slider.addEventListener('scroll', this._scrollHandler, { passive: true });
    }

    teardownMobileScrollSync() {
      const slider = this.getSlider();
      if (slider && this._scrollHandler) {
        slider.removeEventListener('scroll', this._scrollHandler);
      }
      this._scrollHandler = null;
    }

    setupSlideObserver() {
      if (!this.galleryVideoAutoplay || !this.elements.viewer) return;

      const slider = this.getSlider();
      if (!slider) return;

      this.teardownSlideObserver();

      this._slideObserver = new IntersectionObserver(
        (entries) => {
          let bestEntry = null;
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            if (!bestEntry || entry.intersectionRatio > bestEntry.intersectionRatio) {
              bestEntry = entry;
            }
          });

          if (bestEntry && bestEntry.intersectionRatio >= 0.35) {
            this.handleSlideActivate(bestEntry.target);
          }
        },
        { root: slider, threshold: [0.35, 0.5, 0.65, 0.85] }
      );

      this.getVisibleSlides().forEach((slide) => this._slideObserver.observe(slide));
    }

    teardownSlideObserver() {
      if (this._slideObserver) {
        this._slideObserver.disconnect();
        this._slideObserver = null;
      }
    }

    onSlideChanged(event) {
      if (event.detail.currentElement == undefined) return;
      this.handleSlideActivate(event.detail.currentElement);
    }

    handleSlideActivate(slide, options = {}) {
      if (!slide) return;

      const mediaId = slide.dataset.mediaId;
      if (!options.force && this._lastActivatedMediaId === mediaId) return;

      this._lastActivatedMediaId = mediaId;
      clearTimeout(this._activateResetTimer);
      this._activateResetTimer = window.setTimeout(() => {
        this._lastActivatedMediaId = null;
      }, 400);

      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.toggle('is-active', element === slide);
      });

      if (this.elements.thumbnails && mediaId) {
        const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        if (thumbnail) this.setActiveThumbnail(thumbnail);
      }

      if (!this.galleryVideoAutoplay) {
        window.pauseAllMedia();
        return;
      }

      this.pauseGalleryVideosExcept(slide);
      this.playActiveMedia(slide);
    }

    pauseGalleryVideosExcept(activeSlide) {
      this.querySelectorAll('deferred-media[data-gallery-video] video').forEach((video) => {
        if (!activeSlide.contains(video)) {
          video.pause();
          try {
            video.currentTime = 0;
          } catch (error) {
            /* ignore */
          }
        }
      });

      window.pauseAllMedia();
    }

    setActiveMedia(mediaId, prepend, scrollIntoView = true) {
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      if (!activeMedia) return;

      if (prepend) {
        activeMedia.classList.add('is-active');
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          if (element !== activeMedia) element.classList.remove('is-active');
        });
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        this.handleSlideActivate(activeMedia, { force: true });
        if (this.mobileMql.matches) {
          this.setupSlideObserver();
          this.setupMobileScrollSync();
        }
      } else {
        this.handleSlideActivate(activeMedia, { force: true });
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        if ((!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') && scrollIntoView) {
          activeMedia.scrollIntoView({behavior: 'smooth'});
        }
      });

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      if (!activeThumbnail) return;
      this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      this.dispatchEvent(new CustomEvent('mediaChanged', {
        detail: { mediaId: mediaId },
      }));
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('[id^="Slide-"]').forEach((element) => {
        element.classList.remove('is-active');
        element.querySelector('button').removeAttribute('aria-current');
      });
      thumbnail.classList.add('is-active');
      thumbnail.querySelector('button').setAttribute('aria-current', true);

      if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

      this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
    }

    activateMediaById(mediaId) {
      if (!mediaId) return;
      const thumbnailTrigger = this.elements.thumbnails && this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"] button`);
      if (thumbnailTrigger) {
        thumbnailTrigger.click();
        return;
      }
      this.setActiveMedia(mediaId, false);
    }

    stepActiveMedia(step, event) {
      if (event) event.preventDefault();
      if (!this.elements.thumbnails) return;

      const visibleThumbItems = Array.from(this.elements.thumbnails.querySelectorAll('[data-target]')).filter((item) => {
        return window.getComputedStyle(item).display !== 'none';
      });
      if (visibleThumbItems.length < 2) return;

      const activeThumb = this.elements.thumbnails.querySelector('[data-target].is-active');
      const activeIndex = Math.max(visibleThumbItems.indexOf(activeThumb), 0);
      const nextIndex = (activeIndex + step + visibleThumbItems.length) % visibleThumbItems.length;
      const nextTarget = visibleThumbItems[nextIndex] && visibleThumbItems[nextIndex].dataset.target;
      if (!nextTarget) return;

      this.activateMediaById(nextTarget);
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = theme.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem, attempt = 0) {
      const inlineMedia = activeItem.querySelector('deferred-media[data-gallery-video]');
      const deferredMedia = inlineMedia || activeItem.querySelector('deferred-media');

      if (!deferredMedia) return;

      if (!inlineMedia && !deferredMedia.hasAttribute('loaded')) {
        deferredMedia.loadContent(false);
      }

      const video = deferredMedia.querySelector('video');
      const youtube = deferredMedia.querySelector('iframe.js-youtube');
      const vimeo = deferredMedia.querySelector('iframe.js-vimeo');

      if (!video && !youtube && !vimeo) {
        if (attempt < 15) {
          window.setTimeout(() => this.playActiveMedia(activeItem, attempt + 1), 120);
        }
        return;
      }

      this.resumeDeferredMedia(deferredMedia);
    }

    resumeDeferredMedia(deferredMedia) {
      const video = deferredMedia.querySelector('video');
      if (video) {
        video.muted = true;
        video.defaultMuted = true;
        video.setAttribute('muted', '');
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');
        video.loop = video.hasAttribute('loop');

        const playPromise = video.play();
        if (playPromise !== undefined) {
          playPromise.catch(() => {
            window.setTimeout(() => video.play().catch(() => {}), 200);
          });
        }
        return;
      }

      const youtube = deferredMedia.querySelector('iframe.js-youtube');
      if (youtube) {
        window.setTimeout(() => {
          youtube.contentWindow?.postMessage('{"event":"command","func":"mute","args":""}', '*');
          youtube.contentWindow?.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
        }, 250);
        return;
      }

      const vimeo = deferredMedia.querySelector('iframe.js-vimeo');
      if (vimeo) {
        window.setTimeout(() => {
          vimeo.contentWindow?.postMessage('{"method":"setVolume","value":0}', '*');
          vimeo.contentWindow?.postMessage('{"method":"play"}', '*');
        }, 250);
      }
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  });
}
